'use client';

import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatsCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  trend?: number;
  trendLabel?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const variantStyles = {
  default: 'from-[#3B82F6] to-[#8B5CF6]',
  success: 'from-[#10B981] to-[#64FFDA]',
  warning: 'from-[#F59E0B] to-[#EF4444]',
  danger: 'from-[#EF4444] to-[#F59E0B]',
};

const iconBgStyles = {
  default: 'bg-[#3B82F6]/20 text-[#3B82F6]',
  success: 'bg-[#10B981]/20 text-[#10B981]',
  warning: 'bg-[#F59E0B]/20 text-[#F59E0B]',
  danger: 'bg-[#EF4444]/20 text-[#EF4444]',
};

export function StatsCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  trendLabel,
  variant = 'default',
}: StatsCardProps) {
  const isPositive = trend && trend > 0;
  const isNegative = trend && trend < 0;

  return (
    <div className="relative p-6 rounded-xl bg-[#112240] border border-[#1d3557] card-hover overflow-hidden">
      {/* Gradient accent */}
      <div className={cn("absolute top-0 left-0 w-full h-1 bg-gradient-to-r", variantStyles[variant])} />
      
      <div className="flex items-start justify-between">
        <div className="space-y-3">
          <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", iconBgStyles[variant])}>
            <Icon className="w-6 h-6" />
          </div>
          
          <div>
            <p className="text-sm text-[#8892B0] font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-[#E6F1FF] mt-1">{value}</h3>
            
            {description && (
              <p className="text-xs text-[#8892B0] mt-1">{description}</p>
            )}
            
            {trend !== undefined && (
              <div className="flex items-center gap-1 mt-2">
                {isPositive ? (
                  <TrendingUp className="w-3 h-3 text-[#10B981]" />
                ) : isNegative ? (
                  <TrendingDown className="w-3 h-3 text-[#EF4444]" />
                ) : null}
                <span className={cn(
                  "text-xs font-medium",
                  isPositive ? "text-[#10B981]" : isNegative ? "text-[#EF4444]" : "text-[#8892B0]"
                )}>
                  {trend > 0 ? '+' : ''}{trend}%
                </span>
                {trendLabel && (
                  <span className="text-xs text-[#8892B0]">{trendLabel}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Decorative glow */}
      <div className={cn(
        "absolute -bottom-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-20",
        variant === 'default' && "bg-[#3B82F6]",
        variant === 'success' && "bg-[#10B981]",
        variant === 'warning' && "bg-[#F59E0B]",
        variant === 'danger' && "bg-[#EF4444]"
      )} />
    </div>
  );
}
